import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { api, API_URL } from "./api";
import { useSessao } from "./sessao";

type Ouvinte = (dados: unknown) => void;
const ouvintes = new Map<string, Set<Ouvinte>>();

/** Inscreve-se num evento do stream (ex.: "numero.status"). Devolve o cancelamento. */
export function aoEvento(evento: string, ouvinte: Ouvinte): () => void {
  let grupo = ouvintes.get(evento);
  if (!grupo) ouvintes.set(evento, (grupo = new Set()));
  grupo.add(ouvinte);
  return () => grupo.delete(ouvinte);
}

const EVENTOS = [
  "numero.status",
  "numero.qrcode",
  "mensagem.nova",
  "mensagem.status",
  "campanha.progresso",
  "economia.atualizada",
  "lead.atualizado",
] as const;

/**
 * Mantém UMA conexão SSE enquanto houver sessão de conta. Pede um ticket de 60s
 * (o EventSource não envia Authorization) e reconecta sozinho com novo ticket.
 * Cada evento invalida as queries do TanStack Query que dependem dele.
 */
export function useTempoReal(): void {
  const qc = useQueryClient();
  const temConta = useSessao((s) => Boolean(s.accessToken && s.conta));

  useEffect(() => {
    if (!temConta) return;
    let fonte: EventSource | null = null;
    let parado = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const abrir = async () => {
      try {
        const { data } = await api.post<{ ticket: string }>("/eventos/ticket");
        if (parado) return;
        fonte = new EventSource(`${API_URL}/eventos/stream?ticket=${encodeURIComponent(data.ticket)}`);
        for (const nome of EVENTOS) {
          fonte.addEventListener(nome, (e) => {
            let dados: unknown = null;
            try {
              dados = JSON.parse((e as MessageEvent<string>).data);
            } catch {
              /* payload não-JSON: segue sem dados */
            }
            void qc.invalidateQueries({ queryKey: [nome.split(".")[0]] });
            ouvintes.get(nome)?.forEach((fn) => fn(dados));
          });
        }
        fonte.onerror = () => {
          // O ticket vale 60s: ao cair, fecha e reabre com um novo.
          fonte?.close();
          if (!parado) timer = setTimeout(abrir, 3000);
        };
      } catch {
        if (!parado) timer = setTimeout(abrir, 5000);
      }
    };
    void abrir();
    return () => {
      parado = true;
      clearTimeout(timer);
      fonte?.close();
    };
  }, [temConta, qc]);
}
